import { Injectable } from '@angular/core';

export interface ImageCompressOptions {
  maxDimension?: number; // default 800px
  maxSizeKB?: number;    // default 200KB
  outputFormat?: 'image/jpeg' | 'image/webp' | 'image/png'; // default image/jpeg
  initialQuality?: number; // default 0.85
  minQuality?: number;     // default 0.4
}

export interface CompressedImageResult {
  file: File;
  dataUrl: string;
  metaData: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
    lastModifiedDate?: Date;
    webkitRelativePath?: string;
  };
  originalSize: number;
  compressedSize: number;
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageCompressorService {

  /**
   * Compresses an image file client-side using HTML5 Canvas.
   * Resizes dynamically to maxDimension (default 800px) keeping aspect ratio.
   * Compresses adaptively to remain under maxSizeKB (default 200KB).
   */
  public compressImage(file: File, options?: ImageCompressOptions): Promise<CompressedImageResult> {
    return new Promise((resolve, reject) => {
      if (!file) {
        return reject(new Error('No file provided.'));
      }

      if (!file.type || !file.type.startsWith('image/')) {
        return reject(new Error('Invalid image file format'));
      }

      const maxDimension = options && options.maxDimension ? options.maxDimension : 800;
      const targetMaxSizeBytes = (options && options.maxSizeKB ? options.maxSizeKB : 200) * 1024;
      const outputFormat = options && options.outputFormat ? options.outputFormat : 'image/jpeg';
      const initialQuality = options && options.initialQuality !== undefined ? options.initialQuality : 0.85;
      const minQuality = options && options.minQuality !== undefined ? options.minQuality : 0.4;

      const reader = new FileReader();
      reader.onerror = (err) => reject(err);
      reader.onload = (readerEvent: any) => {
        const img = new Image();
        img.onerror = (err) => reject(new Error('Failed to parse image data'));
        img.onload = () => {
          try {
            // Calculate scaled dimensions while preserving aspect ratio
            let width = img.width;
            let height = img.height;

            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
              } else {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
              }
            }

            // Create offscreen canvas
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              return reject(new Error('Canvas 2D context not available'));
            }

            // Draw image on canvas (handles white background for transparent PNGs converted to JPEG)
            if (outputFormat === 'image/jpeg') {
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, width, height);
            }
            ctx.drawImage(img, 0, 0, width, height);

            // Adaptive compression
            let quality = initialQuality;
            let dataUrl = canvas.toDataURL(outputFormat, quality);
            let blob = this.dataURLToBlob(dataUrl);

            // If still larger than targetMaxSizeBytes and quality can be reduced, reduce quality in steps
            while (blob.size > targetMaxSizeBytes && quality > minQuality) {
              quality = Math.max(minQuality, quality - 0.1);
              dataUrl = canvas.toDataURL(outputFormat, quality);
              blob = this.dataURLToBlob(dataUrl);
            }

            const newFileName = this.getFormattedFileName(file.name, outputFormat);
            const compressedFile = new File([blob], newFileName, {
              type: outputFormat,
              lastModified: Date.now()
            });

            const metaData = {
              name: newFileName,
              size: compressedFile.size,
              type: outputFormat,
              lastModified: compressedFile.lastModified,
              lastModifiedDate: new Date(compressedFile.lastModified),
              webkitRelativePath: (file as any).webkitRelativePath || ''
            };

            resolve({
              file: compressedFile,
              dataUrl: dataUrl,
              metaData: metaData,
              originalSize: file.size,
              compressedSize: compressedFile.size,
              width: width,
              height: height
            });
          } catch (e) {
            reject(e);
          }
        };
        img.src = readerEvent.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  private dataURLToBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  private getFormattedFileName(originalName: string, outputFormat: string): string {
    const ext = outputFormat === 'image/webp' ? '.webp' : outputFormat === 'image/png' ? '.png' : '.jpg';
    const dotIndex = originalName.lastIndexOf('.');
    if (dotIndex !== -1) {
      return originalName.substring(0, dotIndex) + ext;
    }
    return originalName + ext;
  }
}
